/**
 * Composition hook for notification filters, actions, and mutations.
 * Combines filter state management with notification mutation actions
 * (mark read/unread, delete, navigate).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentOrganizationId } from "@/hooks/organization";
import { useLogger } from "@/hooks/useLogger";
import { useNotificationFiltersStore } from "@/stores/useNotificationFiltersStore";
import {
	useNotifications,
	useNotificationStats,
	useMarkNotificationRead,
	useMarkNotificationUnread,
	useMarkAllNotificationsRead,
	useDeleteNotification,
	useDeleteAllNotifications,
} from "@/application/queries";
import type { Notification } from "@/domain";
import { getNotificationLink } from "@/utils/notificationRouting";

const SEARCH_DEBOUNCE_MS = 300;

export const useNotificationFilters = () => {
	const orgId = useCurrentOrganizationId();
	const logger = useLogger();
	const navigate = useNavigate();

	// --- Filter state ---
	const filters = useNotificationFiltersStore((s) => s.filters);
	const timeFilter = useNotificationFiltersStore((s) => s.timeFilter);
	const setFilterValue = useNotificationFiltersStore((s) => s.setFilterValue);
	const setTimeFilter = useNotificationFiltersStore((s) => s.setTimeFilter);
	const clearFilters = useNotificationFiltersStore((s) => s.clearFilters);

	// --- Queries ---
	const {
		data,
		isLoading: loading,
		error: queryError,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useNotifications(orgId, filters);
	const { data: stats } = useNotificationStats(orgId);

	const hasActiveFilters = !!(
		filters.is_read !== undefined ||
		filters.notification_type ||
		filters.search ||
		filters.created_after ||
		filters.created_before
	);

	const notifications = useMemo(
		() => data?.pages.flatMap((p) => p.notifications) ?? [],
		[data],
	);
	const totalCount = data?.pages[data.pages.length - 1]?.total_count ?? 0;
	const error = queryError ? (queryError as Error).message : null;

	// --- Mutations ---
	const markReadMutation = useMarkNotificationRead(orgId);
	const markUnreadMutation = useMarkNotificationUnread(orgId);
	const markAllReadMutation = useMarkAllNotificationsRead(orgId);
	const deleteMutation = useDeleteNotification(orgId);
	const deleteAllMutation = useDeleteAllNotifications(orgId);

	// --- Search debounce ---
	const [localSearch, setLocalSearch] = useState(filters.search || "");
	const [debouncedSearch, setDebouncedSearch] = useState(filters.search || "");

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(localSearch);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [localSearch]);

	useEffect(() => {
		if (debouncedSearch !== (filters.search || "")) {
			setFilterValue("search", debouncedSearch || undefined);
		}
	}, [debouncedSearch, setFilterValue, filters.search]);

	// --- Filter handlers ---
	const setSearch = useCallback(
		(search: string) => {
			setLocalSearch(search);
		},
		[setLocalSearch],
	);

	const setReadFilter = useCallback(
		(value: boolean | undefined) => {
			setFilterValue("is_read", value);
		},
		[setFilterValue],
	);

	const setTypeFilter = useCallback(
		(value: string | undefined) => {
			setFilterValue("notification_type", value);
		},
		[setFilterValue],
	);

	const clearAllFilters = useCallback(() => {
		setLocalSearch("");
		setDebouncedSearch("");
		clearFilters();
	}, [clearFilters]);

	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	// --- Mutation handlers ---
	const handleMarkAsRead = useCallback(
		async (uuid: string) => {
			try {
				await markReadMutation.mutateAsync(uuid);
			} catch (error) {
				logger.error("Failed to mark notification as read:", error);
			}
		},
		[markReadMutation.mutateAsync, logger],
	);

	const handleMarkAsUnread = useCallback(
		async (uuid: string) => {
			try {
				await markUnreadMutation.mutateAsync(uuid);
			} catch (error) {
				logger.error("Failed to mark notification as unread:", error);
			}
		},
		[markUnreadMutation.mutateAsync, logger],
	);

	const handleMarkAllAsRead = useCallback(async () => {
		try {
			await markAllReadMutation.mutateAsync();
		} catch (error) {
			logger.error("Failed to mark all notifications as read:", error);
		}
	}, [markAllReadMutation.mutateAsync, logger]);

	const handleDelete = useCallback(
		async (uuid: string) => {
			try {
				await deleteMutation.mutateAsync(uuid);
			} catch (error) {
				logger.error("Failed to delete notification:", error);
			}
		},
		[deleteMutation.mutateAsync, logger],
	);

	const handleDeleteAll = useCallback(async () => {
		try {
			await deleteAllMutation.mutateAsync();
		} catch (error) {
			logger.error("Failed to delete all notifications:", error);
		}
	}, [deleteAllMutation.mutateAsync, logger]);

	const handleNotificationClick = useCallback(
		async (notification: Notification) => {
			if (!notification.is_read) {
				await handleMarkAsRead(notification.uuid);
			}
			const link = getNotificationLink(notification);
			if (link) navigate(link);
		},
		[handleMarkAsRead, navigate],
	);

	return {
		// Data
		notifications,
		stats: stats || null,
		loading,
		error,
		totalCount,
		filters,
		hasActiveFilters,
		localSearch,
		// Infinite scroll
		loadMore,
		hasMore: !!hasNextPage,
		loadingMore: isFetchingNextPage,
		// Time filter
		timeFilter,
		setTimeFilter,
		// Filter actions
		setSearch,
		setReadFilter,
		setTypeFilter,
		clearAllFilters,
		// Mutation actions
		handleMarkAsRead,
		handleMarkAsUnread,
		handleMarkAllAsRead,
		handleDelete,
		handleDeleteAll,
		handleNotificationClick,
	};
};
